export type TimeRange = {
  from: Date;
  to: Date;
};

export type TimeRangePresetId = "last_15m" | "last_1h" | "last_24h";

export type TimeRangePreset = {
  id: TimeRangePresetId;
  label: string;
  compute: () => TimeRange;
};

export const toRFC3339 = (date: Date): string => {
  return date.toISOString();
};

export const fromRFC3339 = (value: string | null | undefined): Date | null => {
  if (!value) return null;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
};

/**
 * Parses a relative time duration string like "5m", "1h", "1d 8h 15m"
 * Returns milliseconds
 */
export const parseRelativeTime = (value: string): number | null => {
  if (!value || value.trim() === "") return null;

  const trimmed = value.trim().toLowerCase();
  let totalMs = 0;

  // Match patterns like "1d", "8h", "15m", "30s"
  const patterns = [
    { regex: /(\d+)\s*d(?:ays?)?/i, multiplier: 24 * 60 * 60 * 1000 },
    { regex: /(\d+)\s*h(?:ours?)?/i, multiplier: 60 * 60 * 1000 },
    { regex: /(\d+)\s*m(?:in(?:utes?)?)?/i, multiplier: 60 * 1000 },
    { regex: /(\d+)\s*s(?:ec(?:onds?)?)?/i, multiplier: 1000 },
  ];

  for (const { regex, multiplier } of patterns) {
    const matches = trimmed.match(regex);
    if (matches) {
      const amount = parseInt(matches[1], 10);
      if (!Number.isNaN(amount)) {
        totalMs += amount * multiplier;
      }
    }
  }

  return totalMs > 0 ? totalMs : null;
};

/**
 * Checks if a string is "now"
 */
export const isNow = (value: string | null | undefined): boolean => {
  return value?.toLowerCase().trim() === "now";
};

/**
 * Checks if a string is a relative time range
 */
export const isRelativeTime = (value: string | null | undefined): boolean => {
  if (!value) return false;
  return parseRelativeTime(value) !== null;
};

/**
 * Checks if a string is an RFC3339 date
 */
export const isRFC3339Date = (value: string | null | undefined): boolean => {
  if (!value) return false;
  const date = fromRFC3339(value);
  return date !== null;
};

export const TIME_RANGE_PRESETS: TimeRangePreset[] = [
  {
    id: "last_15m",
    label: "Letzte 15 Minuten",
    compute: () => {
      const to = new Date();
      const from = new Date(to.getTime() - 15 * 60 * 1000);
      return { from, to };
    },
  },
  {
    id: "last_1h",
    label: "Letzte 1 Stunde",
    compute: () => {
      const to = new Date();
      const from = new Date(to.getTime() - 60 * 60 * 1000);
      return { from, to };
    },
  },
  {
    id: "last_24h",
    label: "Letzte 24 Stunden",
    compute: () => {
      const to = new Date();
      const from = new Date(to.getTime() - 24 * 60 * 60 * 1000);
      return { from, to };
    },
  },
];

/**
 * Parses a human-readable date string like "6 jan 26 14:03"
 * Returns a Date object or null if parsing fails
 */
export const parseHumanDate = (value: string): Date | null => {
  if (!value || value.trim() === "") return null;

  const trimmed = value.trim();

  // Try various date formats
  // Format: "6 jan 26 14:03" or "6 jan 2026 14:03" or "06 jan 26 14:03"
  const patterns = [
    /(\d{1,2})\s+(jan|feb|mar|apr|may|jun|jul|aug|sep|oct|nov|dec)\s+(\d{2,4})\s+(\d{1,2}):(\d{2})/i,
    /(\d{1,2})\/(\d{1,2})\/(\d{2,4})\s+(\d{1,2}):(\d{2})/i,
    /(\d{4})-(\d{1,2})-(\d{1,2})\s+(\d{1,2}):(\d{2})/i,
  ];

  const monthMap: Record<string, number> = {
    jan: 0,
    feb: 1,
    mar: 2,
    apr: 3,
    may: 4,
    jun: 5,
    jul: 6,
    aug: 7,
    sep: 8,
    oct: 9,
    nov: 10,
    dec: 11,
  };

  for (const pattern of patterns) {
    const match = trimmed.match(pattern);
    if (match) {
      if (pattern === patterns[0]) {
        // "6 jan 26 14:03" format
        const day = parseInt(match[1], 10);
        const monthName = match[2].toLowerCase();
        const year = parseInt(match[3], 10);
        const hour = parseInt(match[4], 10);
        const minute = parseInt(match[5], 10);

        const month = monthMap[monthName];
        if (month === undefined) continue;

        const fullYear = year < 100 ? 2000 + year : year;
        const date = new Date(fullYear, month, day, hour, minute, 0, 0);
        if (!Number.isNaN(date.getTime())) {
          return date;
        }
      } else if (pattern === patterns[1]) {
        // "6/1/26 14:03" format
        const month = parseInt(match[1], 10) - 1;
        const day = parseInt(match[2], 10);
        const year = parseInt(match[3], 10);
        const hour = parseInt(match[4], 10);
        const minute = parseInt(match[5], 10);

        const fullYear = year < 100 ? 2000 + year : year;
        const date = new Date(fullYear, month, day, hour, minute, 0, 0);
        if (!Number.isNaN(date.getTime())) {
          return date;
        }
      } else if (pattern === patterns[2]) {
        // "2026-01-06 14:03" format
        const year = parseInt(match[1], 10);
        const month = parseInt(match[2], 10) - 1;
        const day = parseInt(match[3], 10);
        const hour = parseInt(match[4], 10);
        const minute = parseInt(match[5], 10);

        const date = new Date(year, month, day, hour, minute, 0, 0);
        if (!Number.isNaN(date.getTime())) {
          return date;
        }
      }
    }
  }

  return null;
};

/**
 * Parses a time range string with various formats:
 * - "5m" or "-5m" => from = 5m (relative), stop = now
 * - "6 jan 26 14:03+1d" => from = 2026-01-06T14:03:00Z, stop = 2026-01-07T14:03:00Z
 * - "6 jan 26 14:03 - 7 jan 26 14:03" => from = 2026-01-06T14:03:00Z, stop = 2026-01-07T14:03:00Z
 */
export const parseTimeRangeString = (value: string): { from: string; stop: string } | null => {
  if (!value || value.trim() === "") return null;

  const trimmed = value.trim();

  // Pattern 1: "5m" or "-5m" => from = 5m (relative string), stop = now
  if (/^-?\d+[dhms]/.test(trimmed)) {
    const relativeStr = trimmed.replace(/^-/, "");
    const relativeMs = parseRelativeTime(relativeStr);
    if (relativeMs !== null) {
      return {
        from: relativeStr, // Store as relative time string like "5m"
        stop: "now",
      };
    }
  }

  // Pattern 2: "6 jan 26 14:03+1d" => from = date, stop = date + duration
  const durationMatch = trimmed.match(/^(.+?)\+(\d+[dhms])/);
  if (durationMatch) {
    const dateStr = durationMatch[1].trim();
    const durationStr = durationMatch[2];
    const fromDate = parseHumanDate(dateStr);
    const durationMs = parseRelativeTime(durationStr);

    if (fromDate && durationMs !== null) {
      // Round to nearest minute (remove seconds/milliseconds)
      fromDate.setSeconds(0, 0);
      const stopDate = new Date(fromDate.getTime() + durationMs);
      stopDate.setSeconds(0, 0);
      return {
        from: fromDate.toISOString(),
        stop: stopDate.toISOString(),
      };
    }
  }

  // Pattern 3: "6 jan 26 14:03 - 7 jan 26 14:03" => from = date1, stop = date2
  const rangeMatch = trimmed.match(/^(.+?)\s*-\s*(.+)$/);
  if (rangeMatch) {
    const fromStr = rangeMatch[1].trim();
    const stopStr = rangeMatch[2].trim();
    const fromDate = parseHumanDate(fromStr);
    const stopDate = parseHumanDate(stopStr);

    if (fromDate && stopDate) {
      // Round to nearest minute (remove seconds/milliseconds)
      fromDate.setSeconds(0, 0);
      stopDate.setSeconds(0, 0);
      return {
        from: fromDate.toISOString(),
        stop: stopDate.toISOString(),
      };
    }
  }

  return null;
};

export const getPresetById = (id: TimeRangePresetId | null | undefined): TimeRangePreset | null => {
  if (!id) return null;
  return TIME_RANGE_PRESETS.find((p) => p.id === id) ?? null;
};
